import React from 'react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { Box, Container, Typography, Paper, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { safeRedirectPath } from '../utils/redirect';

export const LoginPage: React.FC = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = React.useState<string | null>(null);

  // The path ProtectedRoute turned away, if any — validated, and '/' when absent
  // or untrustworthy (sc-6158). Both login paths below honor it.
  //
  // This works only because the GoogleLogin widget below hands us a credential IN
  // PAGE: there is no full-page bounce to the provider, so `location.state` is
  // still here when we resolve it. Replacing this with a redirect-based flow breaks
  // that silently — see the DEPENDENCY note in utils/redirect.ts for the fix.
  const redirectTo = safeRedirectPath((location.state as { from?: unknown } | null)?.from);

  // Redirect when authenticated (handles One Tap login from AuthContext)
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setError('Failed to receive credentials from Google');
      return;
    }

    try {
      await login(credentialResponse.credential);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      console.error('Error processing Google login:', err);
      setError(err instanceof Error ? err.message : 'Failed to process login. Please try again.');
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in failed. Please try again.');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%', textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Edge Admin
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Sign in with your Google account
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
            />
          </Box>

          <Typography variant="caption" color="textSecondary" sx={{ mt: 3, display: 'block' }}>
            Access is restricted to @hexacontainment.com email addresses
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};
